/**
 * Collections Tab
 *
 * Manages vector database collections with search/upload functionality
 */
import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { knowledgeBaseStore } from '@/stores/KnowledgeBaseStore'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import {
  BookOpenIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  DocumentArrowUpIcon,
  XCircleIcon,
  FolderIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const CollectionsTab: React.FC = observer(() => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showSearchModal, setShowSearchModal] = useState(false)
  const [selectedCollectionName, setSelectedCollectionName] = useState<string | null>(null)

  useEffect(() => {
    knowledgeBaseStore.fetchCollections()
  }, [])

  const handleSelectCollection = async (name: string) => {
    setSelectedCollectionName(name)
    await knowledgeBaseStore.fetchCollection(name)
  }

  const handleDeleteCollection = async (name: string) => {
    if (window.confirm(`Are you sure you want to delete collection "${name}"?`)) {
      try {
        await knowledgeBaseStore.deleteCollection(name)
        toast.success('Collection deleted')
        if (selectedCollectionName === name) {
          setSelectedCollectionName(null)
        }
      } catch (error) {
        toast.error('Failed to delete collection')
      }
    }
  }

  const handleCreateSuccess = () => {
    setShowCreateModal(false)
    toast.success('Collection created successfully')
  }

  const handleUploadSuccess = () => {
    setShowUploadModal(false)
    toast.success('Documents uploaded successfully')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {knowledgeBaseStore.collections.length} collections
        </p>
        <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
          <PlusIcon className="w-4 h-4 mr-1" />
          Create Collection
        </Button>
      </div>

      {/* Collections Grid */}
      {knowledgeBaseStore.isLoading && knowledgeBaseStore.collections.length === 0 ? (
        <Card className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading collections...</p>
        </Card>
      ) : !knowledgeBaseStore.hasCollections ? (
        <Card className="text-center py-12">
          <BookOpenIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Collections Yet</h3>
          <p className="text-gray-600 mb-4">
            Create your first collection to start managing documents
          </p>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <PlusIcon className="w-4 h-4 mr-1" />
            Create Collection
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {knowledgeBaseStore.collections.map((collectionName) => (
            <Card
              key={collectionName}
              className={clsx(
                'hover:shadow-lg transition-shadow cursor-pointer',
                selectedCollectionName === collectionName && 'ring-2 ring-primary-500'
              )}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary-500/15 rounded-lg">
                    <FolderIcon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{collectionName}</h3>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteCollection(collectionName)
                  }}
                  className="text-gray-400 hover:text-red-600 transition-colors"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleSelectCollection(collectionName)}
                  className="w-full"
                >
                  View Details
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedCollectionName(collectionName)
                      setShowUploadModal(true)
                    }}
                    className="flex-1"
                  >
                    <DocumentArrowUpIcon className="w-4 h-4 mr-1" />
                    Upload
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedCollectionName(collectionName)
                      setShowSearchModal(true)
                    }}
                    className="flex-1"
                  >
                    <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                    Search
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Collection Details */}
      {selectedCollectionName && knowledgeBaseStore.selectedCollection && (
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              Collection: {selectedCollectionName}
            </h2>
            <button
              onClick={() => {
                setSelectedCollectionName(null)
                knowledgeBaseStore.clearSelectedCollection()
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <p className="text-sm text-blue-600 mb-1">Vectors Count</p>
              <p className="text-2xl font-bold text-blue-600">
                {knowledgeBaseStore.selectedCollection.vectors_count?.toLocaleString() || 0}
              </p>
            </div>
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
              <p className="text-sm text-green-600 mb-1">Points Count</p>
              <p className="text-2xl font-bold text-green-600">
                {knowledgeBaseStore.selectedCollection.points_count?.toLocaleString() || 0}
              </p>
            </div>
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
              <p className="text-sm text-purple-600 mb-1">Status</p>
              <Badge variant="success">
                {knowledgeBaseStore.selectedCollection.status || 'Active'}
              </Badge>
            </div>
          </div>

          {knowledgeBaseStore.selectedCollection.config && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Configuration</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600">Vector Size</p>
                    <p className="text-sm font-medium text-gray-900">
                      {knowledgeBaseStore.selectedCollection.config.vector_size}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Distance Metric</p>
                    <p className="text-sm font-medium text-gray-900">
                      {knowledgeBaseStore.selectedCollection.config.distance}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateCollectionModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {showUploadModal && selectedCollectionName && (
        <UploadDocumentsModal
          collectionName={selectedCollectionName}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {showSearchModal && selectedCollectionName && (
        <SearchModal collectionName={selectedCollectionName} onClose={() => setShowSearchModal(false)} />
      )}
    </div>
  )
})

// Create Collection Modal
const CreateCollectionModal = observer(
  ({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) => {
    const [name, setName] = useState('')
    const [vectorSize, setVectorSize] = useState(1536)
    const [distance, setDistance] = useState('cosine')

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()

      try {
        await knowledgeBaseStore.createCollection({
          name,
          vector_size: vectorSize,
          distance,
        })
        onSuccess()
      } catch (error) {
        toast.error('Failed to create collection')
      }
    }

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <Card className="w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Create Collection</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Collection Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="my-collection"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vector Size</label>
                <input
                  type="number"
                  value={vectorSize}
                  onChange={(e) => setVectorSize(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Default: 1536 (OpenAI text-embedding-ada-002)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Distance Metric
                </label>
                <select
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="cosine">Cosine</option>
                  <option value="euclidean">Euclidean</option>
                  <option value="dot">Dot Product</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!name.trim() || knowledgeBaseStore.isLoading}
                  isLoading={knowledgeBaseStore.isLoading}
                >
                  Create Collection
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    )
  }
)

// Upload Documents Modal
const UploadDocumentsModal = observer(
  ({
    collectionName,
    onClose,
    onSuccess,
  }: {
    collectionName: string
    onClose: () => void
    onSuccess: () => void
  }) => {
    const [documents, setDocuments] = useState('')

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (event) => {
        const content = event.target?.result as string
        setDocuments(content)
      }
      reader.readAsText(file)
    }

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()

      const docTexts = documents
        .split('\n\n')
        .filter((text) => text.trim())
        .map((text) => ({ text: text.trim() }))

      if (docTexts.length === 0) {
        toast.error('Please enter at least one document')
        return
      }

      try {
        await knowledgeBaseStore.insertDocuments(collectionName, docTexts)
        onSuccess()
      } catch (error) {
        toast.error('Failed to upload documents')
      }
    }

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <Card className="w-full max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Upload Documents to {collectionName}
              </h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload File</label>
                <input
                  type="file"
                  accept=".txt,.md,.json"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Supported formats: .txt, .md, .json</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Or paste text (one document per paragraph)
                </label>
                <textarea
                  value={documents}
                  onChange={(e) => setDocuments(e.target.value)}
                  placeholder="Paste your documents here... Separate documents with blank lines."
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {knowledgeBaseStore.isUploading && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-700">Uploading...</span>
                    <span className="text-sm font-medium text-gray-900">
                      {knowledgeBaseStore.uploadProgress}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all"
                      style={{ width: `${knowledgeBaseStore.uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!documents.trim() || knowledgeBaseStore.isUploading}
                  isLoading={knowledgeBaseStore.isUploading}
                >
                  <DocumentArrowUpIcon className="w-4 h-4 mr-1" />
                  Upload Documents
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    )
  }
)

// Search Modal
const SearchModal = observer(
  ({ collectionName, onClose }: { collectionName: string; onClose: () => void }) => {
    const [query, setQuery] = useState('')
    const [limit, setLimit] = useState(10)

    const handleSearch = async (e: React.FormEvent) => {
      e.preventDefault()

      if (!query.trim()) {
        toast.error('Please enter a search query')
        return
      }

      try {
        await knowledgeBaseStore.searchCollection(collectionName, query, limit)
      } catch (error) {
        toast.error('Search failed')
      }
    }

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Search {collectionName}</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSearch} className="space-y-4 mb-6">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter your search query..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <input
                  type="number"
                  value={limit}
                  onChange={(e) => setLimit(parseInt(e.target.value))}
                  placeholder="Limit"
                  min="1"
                  max="100"
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={knowledgeBaseStore.isSearching}
                  isLoading={knowledgeBaseStore.isSearching}
                >
                  <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                  Search
                </Button>
              </div>
            </form>

            {/* Search Results */}
            {knowledgeBaseStore.searchResults.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Results ({knowledgeBaseStore.searchResults.length})
                </h3>
                {knowledgeBaseStore.searchResults.map((result, index) => (
                  <div key={result.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="primary">#{index + 1}</Badge>
                        <span className="text-sm text-gray-600">
                          Score: {result.score.toFixed(4)}
                        </span>
                      </div>
                      <code className="text-xs bg-gray-200 px-2 py-1 rounded">{result.id}</code>
                    </div>
                    <p className="text-sm text-gray-900 mb-2">{result.text}</p>
                    {result.metadata && Object.keys(result.metadata).length > 0 && (
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Metadata:</span>{' '}
                        {JSON.stringify(result.metadata)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {knowledgeBaseStore.searchQuery &&
              knowledgeBaseStore.searchResults.length === 0 &&
              !knowledgeBaseStore.isSearching && (
                <div className="text-center py-8">
                  <p className="text-gray-600">
                    No results found for "{knowledgeBaseStore.searchQuery}"
                  </p>
                </div>
              )}
          </Card>
        </div>
      </div>
    )
  }
)

CollectionsTab.displayName = 'CollectionsTab'

export default CollectionsTab
